<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { Config_Mode } from '~/types/graphql';

import { useClientGroupsStore } from '~/stores/client-groups';
import { useConfigStore } from '~/stores/config';
import { useConfigParserStore } from '~/stores/configParser';

import SidebarConfig from '~/components/module/SidebarConfig.vue';
import ModuleConfigForm from '~/components/module/ConfigForm.vue';

const { t, locale } = useI18n();
const toast = useToast();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  groupId: {
    type: Number,
    default: null,
  },
  configMode: {
    type: String,
    default: Config_Mode.Production,
  },
  width: {
    type: String,
    default: '70vw',
  },
  height: {
    type: String,
    default: '95vh',
  },
});

const emit = defineEmits<{
  'update:modelValue': [boolean];
}>();

// v-model bridge
const visible = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val),
});

const groupStore = useClientGroupsStore();
const configStore = useConfigStore();
const configParserStore = useConfigParserStore();

const loading = ref(false);
const confighash = ref('');

const languageCode = computed(() => locale.value);

// mirrors page component
const configData = computed(() => configParserStore.getOrganizedConfigData);
const sortedModules = computed(() => configParserStore.getSortedModules);

// helper just like the page's v-if
const hasConfigData = computed(() => {
  const data = configData.value as any;
  return data && Object.keys(data).length > 0;
});

/**
 * Load group data (for config hash).
 */
const loadGroup = async () => {
  if (!props.groupId) return false;

  try {
    const res = await groupStore.getGroup(props.groupId);

    if (!res?.data?.getClientGroup) {
      toast.add({
        severity: 'error',
        summary: t('config.toast.loadError'),
        detail: t('config.toast.noGroupData'),
        life: 3000,
      });
      return false;
    }

    const targetConfig =
      props.configMode === Config_Mode.Staging
        ? (res.data.getClientGroup as any).stagingConfig
        : res.data.getClientGroup.config;

    confighash.value =
      targetConfig?.name ??
      targetConfig?.configMd5Hash ??
      '';

    return true;
  } catch (error) {
    console.error('Error while loading the group data', error);
    toast.add({
      severity: 'error',
      summary: t('config.toast.loadError'),
      detail: t('config.toast.noGroupData'),
      life: 3000,
    });
    return false;
  }
};

/**
 * Load config file id for this group.
 */
const loadConfigFileId = async () => {
  if (!props.groupId) return false;

  try {
    const res = await configStore.getConfigFile(
      props.groupId,
      props.configMode as Config_Mode,
    );

    if (
      !res?.getConfigFile ||
      !res.getConfigFile.structureId ||
      !res.getConfigFile.configId
    ) {
      toast.add({
        severity: 'error',
        summary: t('config.toast.loadError'),
        detail: t('config.toast.noConfigFile'),
        life: 3000,
      });
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error while loading the configuration-ID:', error);
    toast.add({
      severity: 'error',
      summary: t('config.toast.loadError'),
      detail: t('config.toast.noConfigFile'),
      life: 3000,
    });
    return false;
  }
};

/**
 * Load config structure (fills configParserStore).
 */
const loadConfigFileData = async () => {
  if (!configStore.configStructureFileId) return false;

  try {
    const res = await configStore.getConfigFileStructure(
      configStore.configStructureFileId,
      languageCode.value,
    );

    if (!res?.getConfigFileStructure) {
      toast.add({
        severity: 'error',
        summary: t('config.toast.loadError'),
        detail: t('config.toast.noConfigFileData'),
        life: 3000,
      });
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error while loading the config file data', error);
    toast.add({
      severity: 'error',
      summary: t('config.toast.loadError'),
      detail: t('config.toast.noConfigFileData'),
      life: 3000,
    });
    return false;
  }
};

/**
 * Called by ModuleConfigForm via @reload-config.
 */
const reloadConfigFileData = async ({ resolve, reject, }: {
  resolve: () => void;
  reject: (error: Error) => void;
}) => {
  loading.value = true;
  try {
    await loadConfigFileData();
    resolve();
  } catch (err) {
    const error =
      err instanceof Error
        ? err
        : new Error('Unknown error occurred while reloading configuration');
    console.error('Error while reloading configuration data:', error);
    toast.add({
      severity: 'error',
      summary: t('config.toast.reloadError'),
      life: 3000,
    });
    reject(error);
  } finally {
    loading.value = false;
  }
};

/**
 * Full init – same as page initializeData().
 */
const initializeData = async () => {
  if (!props.groupId || isNaN(props.groupId)) {
    console.error('Invalid group ID provided', props.groupId);
    toast.add({
      severity: 'error',
      summary: t('config.toast.loadError'),
      detail: t('config.toast.invalidGroupId'),
      life: 3000,
    });
    return;
  }

  try {
    loading.value = true;

    const groupLoaded = await loadGroup();
    if (!groupLoaded) return;

    const configIdLoaded = await loadConfigFileId();
    if (!configIdLoaded) return;

    try {
      const sessionOpened = await (configStore as any).openEditSession();
      if (!sessionOpened) {
        toast.add({
          severity: 'error',
          summary: t('config.toast.loadError'),
          detail: t('config.toast.noConfigFile'),
          life: 3000,
        });
        visible.value = false;
        return;
      }
    } catch (lockError: any) {
      // Check if this is a lock conflict error
      const errorMessage = lockError?.message || lockError?.graphQLErrors?.[0]?.message || '';
      if (errorMessage.includes('already locked by')) {
        toast.add({
          severity: 'warn',
          summary: t('config.toast.configLocked'),
          detail: errorMessage,
          life: 5000,
        });
      } else {
        toast.add({
          severity: 'error',
          summary: t('config.toast.loadError'),
          detail: t('config.toast.lockError'),
          life: 3000,
        });
      }
      visible.value = false;
      return;
    }

    await loadConfigFileData();
  } catch (error) {
    console.error('Error while initializing the data:', error);
    toast.add({
      severity: 'error',
      summary: t('config.toast.loadError'),
      life: 3000,
    });
  } finally {
    loading.value = false;
  }
};

// Mobile sidebar state
const isMobileSidebarOpen = ref(false);
const isMobile = ref(false);
const userHasToggledSidebar = ref(false);
const wasLastMobile = ref(false);

// Check if mobile
const checkMobile = () => {
  const currentIsMobile = window.innerWidth <= 768;
  const modeChanged = currentIsMobile !== wasLastMobile.value;

  isMobile.value = currentIsMobile;
  wasLastMobile.value = currentIsMobile;

  // Only auto-open on initial mobile load or when switching to mobile for the first time
  if (currentIsMobile && modeChanged && !userHasToggledSidebar.value) {
    isMobileSidebarOpen.value = true;
  } else if (!currentIsMobile) {
    // Reset when going back to desktop
    isMobileSidebarOpen.value = false;
    userHasToggledSidebar.value = false;
  }
};

const toggleMobileSidebar = () => {
  isMobileSidebarOpen.value = !isMobileSidebarOpen.value;
  userHasToggledSidebar.value = true;
};

const closeMobileSidebar = () => {
  isMobileSidebarOpen.value = false;
  userHasToggledSidebar.value = true;
};

// Init once when the dialog component mounts
onMounted(async () => {
  // Initialize mobile check
  checkMobile();
  window.addEventListener('resize', checkMobile);

  if (props.groupId) {
    await initializeData();
  }
});

onUnmounted(async () => {
  window.removeEventListener('resize', checkMobile);
  await (configStore as any).releaseEditSession();
});

// Re-init if groupId changes while mounted
watch(
  () => props.groupId,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      await initializeData();
    }
  },
);

watch(
  () => props.configMode,
  async (newVal, oldVal) => {
    if (visible.value && newVal !== oldVal) {
      await initializeData();
    }
  },
);

// Reload structure if language changes while dialog is open
watch(locale, async () => {
  if (visible.value) {
    loading.value = true;
    await loadConfigFileData();
    loading.value = false;
  }
});

// When dialog becomes visible, check if locale mismatches with store
watch(visible, async (isVisible) => {
  if (!isVisible) {
    // Dialog is closing - session will be released by @hide handler
    return;
  }

  if (isVisible && configStore.currentLocale && configStore.currentLocale !== languageCode.value) {
    console.debug(`Dialog opened with locale mismatch: UI=${languageCode.value}, Store=${configStore.currentLocale}. Reloading...`);
    loading.value = true;
    await loadConfigFileData();
    loading.value = false;
  }
});

// Watch for locale mismatch while dialog is open
watch([() => languageCode.value, () => configStore.currentLocale], ([currentLocale, storeLocale]) => {
  if (visible.value && storeLocale && currentLocale !== storeLocale && !loading.value) {
    // console.debug(`Locale mismatch in dialog: UI=${currentLocale}, Store=${storeLocale}. Reloading...`);
    loading.value = true;
    loadConfigFileData().finally(() => {
      loading.value = false;
    });
  }
});

const handleConfigFormCancel = () => {
  visible.value = false;
};

const handleDialogHide = async () => {
  // Ensure session is released when dialog is hidden by any means
  await (configStore as any).releaseEditSession();
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :header="t('config.form.title') + (confighash ? ': ' + confighash : '')"
    :modal="true"
    :dismissableMask="false"
    :closeOnEscape="false"
    class="config-form-dialog"
    :style="{ width, height }"
    @hide="handleDialogHide"
  >
      <template #header>
          <div class="dialog-header-content">
            <button
              v-if="isMobile"
              class="mobile-sidebar-toggle-btn"
              @click="toggleMobileSidebar"
              :aria-label="t('common.actions.toggleMenu')"
            >
              <i class="pi pi-bars"></i>
            </button>
            <h3 class="dialog-title">{{ t('config.form.title') + (confighash ? ': ' + confighash : '') }}</h3>
          </div>
        </template>

    <div class="dialog-container">
      <div class="dialog-main">
        <!-- Mobile backdrop -->
        <div
          v-if="isMobile && isMobileSidebarOpen"
          class="mobile-sidebar-backdrop"
          @click="closeMobileSidebar"
        ></div>

        <!-- LEFT: sidebar modules -->
        <div
          class="dialog-sidebar"
          :class="{
            'sidebar-open': isMobileSidebarOpen,
            'sidebar-closed': isMobile && !isMobileSidebarOpen
          }"
        >
          <div class="sidebar-header" v-if="isMobile">
            <h4>{{ t('config.sidebar.modules') }}</h4>
            <button class="sidebar-close-btn" @click="closeMobileSidebar" :aria-label="t('common.actions.close')">
              <i class="pi pi-times"></i>
            </button>
          </div>
          <SidebarConfig
            :sorted-modules="sortedModules"
            :loading="loading"
            :show-title="false"
            @module-selected="isMobile && closeMobileSidebar()"
          />
        </div>

        <!-- RIGHT: full ModuleConfigForm (form + footer + undo etc.) -->
        <div class="dialog-content">
          <ModuleConfigForm
            v-if="!loading && hasConfigData"
            :config-data="configData"
            :config-hash="confighash"
            :loading-config="loading"
            @cancel-config-edit="handleConfigFormCancel"
            @reload-config="reloadConfigFileData"
          />
          <div
            v-else
            class="flex justify-content-center align-items-center"
            style="min-height: 300px;"
          >
            <ProgressSpinner />
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.config-form-dialog {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  min-height: 40px;
}

.dialog-title {
  transform: translateX(-50px);
  //margin: 0;
  font-weight: 500;
  flex: 1;
  padding-left: 48px;
}

.mobile-sidebar-toggle-btn {
  display: none; /* Hidden by default on desktop */
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: hsl(var(--primary));
  color: hsl(var(--primary-foreground));
  border: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: absolute;
  top: 8px;
  left: 8px;
}

.mobile-sidebar-toggle-btn:hover {
  background: hsl(var(--primary) / 0.9);
  transform: scale(1.05);
}

.mobile-sidebar-toggle-btn:active {
  transform: scale(0.95);
}

.dialog-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.dialog-main {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.dialog-sidebar {
  width: 250px;
  background-color: hsl(var(--secondary-nav-bg));
  border-right: 1px solid hsl(var(--border));
  overflow-y: auto;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid hsl(var(--border));
  background-color: hsl(var(--secondary-nav-bg));
}

.sidebar-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.sidebar-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: hsl(var(--foreground));
  transition: background 0.2s ease;
}

.sidebar-close-btn:hover {
  background: hsl(var(--card-foreground) / 0.1);
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  position: relative;
}

.mobile-sidebar-backdrop {
  display: none;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .mobile-sidebar-toggle-btn {
    display: flex !important; /* Show on mobile */
  }

  .config-form-dialog {
    width: 100vw !important;
    height: 100vh !important;
    margin: 0;
    max-width: 100vw;
    max-height: 100vh;
  }

  :deep(.p-dialog) {
    width: 100vw !important;
    height: 100vh !important;
    margin: 0;
    max-width: 100vw;
    max-height: 100vh;
  }

  .dialog-main {
    flex-direction: row; /* Keep as row to maintain layout */
  }

  .dialog-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 280px;
    max-width: 280px;
    z-index: 1001;
    transform: translateX(-100%);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }

  .dialog-sidebar.sidebar-open {
    transform: translateX(0);
  }

  .dialog-sidebar.sidebar-closed {
    transform: translateX(-100%);
  }

  .mobile-sidebar-backdrop {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }

  /* Hide nested sidebar toggle inside SidebarConfig on mobile */
  :deep(.mobile-sidebar-toggle) {
    display: none !important;
  }

  /* Ensure SidebarConfig itself is not hidden by its internal mobile classes */
  :deep(.dialog-sidebar .module-sidebar) {
    transform: none !important;
    position: static !important;
    width: 100% !important;
    height: auto !important;
  }

  :deep(.dialog-sidebar .mobile-sidebar-backdrop) {
    display: none !important;
  }

  :deep(.dialog-sidebar .icon-btn-close) {
    display: none !important;
  }

  /* Hide sidebar from layout flow on mobile and style content */
  .dialog-content {
    width: 100% !important;
    flex: 1 1 100% !important;
    padding: 0.5rem;
    padding-bottom: 0 !important;
    overflow-y: auto;
    overflow-x: hidden;
  }

  /* Ensure form fields stack vertically on mobile */
  :deep(.config-main-content .grid),
  :deep(.config-main-content .p-grid),
  :deep(.config-main-content .p-field),
  :deep(.config-main-content .field) {
    display: flex !important;
    flex-direction: column !important;
  }

  :deep(.config-main-content [class*="col-"]),
  :deep(.config-main-content .p-col),
  :deep(.config-main-content .p-col-12),
  :deep(.config-main-content .p-col-6),
  :deep(.config-main-content .p-col-4),
  :deep(.config-main-content .p-col-3) {
    width: 100% !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
    padding: 0.5rem 0 !important;
  }

  /* Make form inputs full width on mobile */
  :deep(.config-main-content .p-inputtext),
  :deep(.config-main-content .p-dropdown),
  :deep(.config-main-content .p-calendar),
  :deep(.config-main-content .p-inputnumber),
  :deep(.config-main-content .p-inputtextarea) {
    width: 100% !important;
  }

  /* Adjust footer to prevent overlap */
  :deep(.configform-footer) {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    z-index: 999;
  }

  :deep(.footer-content) {
    flex-direction: column !important;
    gap: 0.5rem !important;
    padding: 0.75rem !important;
  }

  :deep(.footer-content > div) {
    width: 100%;
    justify-content: center;
  }

  :deep(.footer-content button) {
    width: 100% !important;
  }
}

/* Tablet adjustments */
@media (max-width: 1024px) and (min-width: 769px) {
  .dialog-sidebar {
    width: 200px;
  }

  .dialog-content {
    padding: 0.75rem;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .dialog-sidebar {
    width: 85vw;
  }

  .dialog-content {
    padding: 0.25rem;
  }
}
</style>
